var AbstractAudioPlayer         = require("app/MediaPlayer/Tech/Abstract/AbstractAudioPlayer"),
    PlayerApi                   = require("radiant-mediaplayer-iframe-api").default,
    circularSafeJSONStringify   = require("app/utils/circularSafeJSONStringify"),
    config                      = require("app/config"),
    defaultAudioSetup           = require("app/ui/radiant/defaultAudioSetup"),
    frameThrottle               = require("enyo-perf/utils").frameThrottle,
    get                         = require("object-path").get,
    getAdSetup                  = require("app/ui/radiant/getAdSetup"),
    getCastingSetup             = require("app/ui/radiant/getCastingSetup"),
    getContentMetadataSetup     = require("app/ui/radiant/getContentMetadataSetup"),
    getGoogleAnalyticsSetup     = require("app/ui/radiant/getGoogleAnalyticsSetup"),
    getI18nSetup                = require("app/ui/radiant/getI18nSetup"),
    getOffsetStartPositionSetup = require("app/ui/radiant/getOffsetStartPositionSetup"),
    getSrcSetup                 = require("app/ui/radiant/getSrcSetup"),
    kind                        = require("enyo/kind"),
    logger                      = require("enyo/logger");

require("./Player.less");

module.exports = kind(/** @lends  app.MediaPlayer.RadiantMediaPlayer.IframeAudioPlayer.prototype */ {

    /**
     * @private
     */
    name: "app.MediaPlayer.RadiantMediaPlayer.IframeAudioPlayer",

    /**
     * @private
     */
    kind: AbstractAudioPlayer,

    /**
     * @private
     */
    classes: "app-media-player-radiant-media-player-iframe-audio-player",

    /**
     * This player can apply safe area styling to it's UI.
     *
     * @type  {boolean}
     * @readonly
     * @public
     */
    supportsSafeArea: true,

    /**
     * @type {boolean}
     * @private
     */
    isReusable: true,

    /**
     * An iframe for RadiantMP Iframe Player.
     *
     * @type  {?HTMLIFrameElement}
     * @private
     */
    iframe: null,

    /**
     * @type {boolean}
     * @private
     */
    isLoadingPlayerApi: false,

    /**
     * @type {PlayerApi}
     * @private
     */
    playerApi: null,

    /**
     * @type {boolean}
     * @private
     */
    changeMediaSourceOnInitialize: false,

    /**
     * @type {?String}
     * @private
     */
    autoplayMode: null,

    /**
     * @type  {boolean}
     * @private
     */
    hasPlayedAds: false,

    /**
     * The ID of the video that's currently playing.
     *
     * @type {?string}
     * @private
     */
    mediaId: null,

    /**
     * The Ad Type of the session that's currently playing.
     *
     * @type {?string}
     * @private
     */
    adType: null,

    /**
     * @type  {number}
     * @readonly
     * @public
     */
    hasInitialized: -1,

    /**
     * The state of this page.
     *
     * @type  {?Object}
     * @public
     */
    state: null,

    /**
     * @type {boolean}
     * @public
     */
    autoplay: true,

    /**
     * @type  {boolean}
     * @readonly
     * @public
     */
    isPlaying: false,

    /**
     * @type  {boolean}
     * @readonly
     * @public
     */
    isPlayingAds: false,

    /**
     * @readonly
     * @public
     */
    playerState: "idle",

    /**
     * @private
     */
    observers: {
        update: ["state"]
    },

    /**
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            this.update                 = frameThrottle(this.bindSafely(this.update));
            this.onAdBlock              = this.bindSafely(this.onAdBlock);
            this.onAutoplayModeDetected = this.bindSafely(this.onAutoplayModeDetected);
            this.onCursorChanged        = this.bindSafely(this.onCursorChanged);
            this.onFatalError           = this.bindSafely(this.onFatalError);
            this.onPlayerError          = this.bindSafely(this.onPlayerError);
            this.onSetupError           = this.bindSafely(this.onSetupError);
            this.onStateChange          = this.bindSafely(this.onStateChange);
            sup.apply(this, arguments);
        };
    }),

    /**
     * Caveat: This control assumes it'll only be rendered once.
     *
     * @private
     */
    rendered: kind.inherit(function(sup) {
        return function() {
            var iframe,
                playerApi;

            iframe = this.createPlayerIframe();

            if (iframe) {
                playerApi = new PlayerApi(iframe);
                this.set("playerApi", playerApi);
            }

            sup.apply(this, arguments);
        };
    }),

    /**
     * Creates an Iframe for the player to run in.
     *
     * @returns {HTMLIFrameElement|null} The iframe if created, `null` otherwise
     * @private
     */
    createPlayerIframe: function() {
        var node      = this.hasNode(),
            playerUrl = config.radiantMediaPlayer.iframePlayerUrl,
            iframe    = this.get("iframe") || null;

        // Iframe already exists. Bail early.
        if (iframe) {
            return iframe;
        }

        // No player url, no iframe.
        if (!playerUrl) {
            this.controller.triggerHandler("onMediaPlayerError", {
                type:    "player_setup_error",
                message: "Player url is missing"
            });

            this.set("iframe", null);

            return null;
        }

        if (node) {
            playerUrl = new URL(playerUrl);

            if (typeof __Vikaru !== "undefined" && !__Vikaru.allowTracking) {
                playerUrl.searchParams.set("sentry", "false");
            }
            else if (!this.app.$.sentryIOService.hasLoadConsent()) {
                playerUrl.searchParams.set("sentry", "false");
            }
            else {
                playerUrl.searchParams.set("sentry", "true");
            }

            playerUrl.searchParams.set("v", config.radiantMediaPlayer.version);

            iframe = document.createElement("iframe");
            iframe.setAttribute("frameborder", "0");
            iframe.setAttribute("allowfullcreen", "");
            iframe.setAttribute("allow", "autoplay; fullscreen; encrypted-media;");
            iframe.setAttribute("src", playerUrl.toString());
            node.appendChild(iframe);
            this.set("iframe", iframe);
        }

        return iframe;
    },

    /**
     * @private
     */
    reflow: kind.inherit(function(sup) {
        return function() {
            var playerApi = this.getPlayerApi(),
                bounds;

            sup.apply(this, arguments);

            if (playerApi) {
                bounds = this.getBounds();
                playerApi.setPlayerSize(bounds.width, bounds.height);
            }
        };
    }),

    /**
     * @private
     */
    update: function() {
        var state   = this.state || {},
            mediaId = get(state, "media.id", null),
            playerApi;

        // No mediaId, nothing to play yet.
        if (!mediaId) {
            this.clearPlayer();
            this.resetPlayerState();

            return;
        }

        // Only activate when absoluteShowing
        if (!this.absoluteShowing) {
            this.addToAbsoluteShowingQueue("update");

            return;
        }
        this.removeFromAbsoluteShowingQueue("update");

        playerApi = this.getPlayerApi();

        if (!playerApi) {
            this.controller.triggerHandler("onMediaPlayerError", {
                type:    "player_setup_error",
                message: "Player API is missing"
            });

            return;
        }

        // Wait for the API to be ready. Then initialize a new player if we
        // don't already have one or simply switch to the new media source if we
        // already have a fully initialized player.
        this.set("isLoadingPlayerApi", true);
        playerApi.loaded
            .then(this.bindSafely(function() {
                switch (this.hasInitialized) {
                    case -1:
                        this.initializePlayer();
                        break;
                    case 0:
                        this.changeMediaSourceOnInitialize = true;
                        break;
                    case 1:
                        this.changeMediaSource();
                        break;
                    default:
                        break;
                }
            }))
            .catch(this.bindSafely(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                this.onSetupError({
                    message: error.message
                });
            }))

            .finally(this.bindSafely(function() {
                this.set("isLoadingPlayerApi", false);
            }));
    },

    /**
     * @param  {boolean}  wasLoading  The previous state.
     * @private
     */
    isLoadingPlayerApiChanged: function(wasLoading) {
        var isLoading = this.isLoadingPlayerApi;

        if (!wasLoading && isLoading) {
            this.controller.triggerHandler("onRequestShowLoadingGraphic");
        }
        else if (wasLoading && !isLoading) {
            this.controller.triggerHandler("onRequestHideLoadingGraphic");
        }
    },

    /**
     * Stops playback regardless of current ready state.
     *
     * @private
     */
    clearPlayer: function() {
        var api = this.getPlayerApi();

        api && api.stop();
    },

    /**
     * Resets player state and creates a new Radiant Media Player instance.
     *
     * @private
     */
    initializePlayer: function() {
        var $analyticsService = this.app.$.analyticsService,
            $i18nService      = this.app.$.i18nService,
            $privacyService   = this.app.$.privacyService,
            state             = this.state || {},
            media             = get(state, "media", {}),
            asset             = get(state, "asset", {}),
            session           = get(state, "watchingSession", {}),
            params            = get(state, "params", {}),
            playerApi         = this.getPlayerApi(),
            setup,
            bounds            = this.getBounds();

        // Ok, lets go
        this.resetPlayerState();
        this.set("isReady", 0);
        this.set("hasInitialized", 0);

        // Build player setup
        setup = Object.assign(
            {},
            defaultAudioSetup,
            {
                autoplay:                        false,
                requestAutoplayCapabilitiesData: true,
                viewableAutoplay:                false,
                viewablePlayPause:               false
            },
            {
                width: bounds.width
            },
            getContentMetadataSetup(media, {
                artwork:          true,
                poster:           true,
                thumbnail:        true,
                endOfVideoPoster: true,
                artworkRatio:     "square",
                posterRatio:      "square",
                thumbnailRatio:   "square"
            }),
            getSrcSetup(session, media),
            getI18nSetup($i18nService),
            getCastingSetup(session),
            getOffsetStartPositionSetup(session, media, asset, params),
            getGoogleAnalyticsSetup($analyticsService, media),
            getAdSetup(
                session,
                media,
                asset,
                $i18nService,
                $privacyService
            )
        );

        if (!setup.src) {
            this.controller.triggerHandler("onMediaPlayerError", {
                type: "missing_audio_source"
            }, this);

            return;
        }

        // Bind event listeners
        this.set("mediaId", get(state, "media.id", null));
        this.set("adType", get(state, "session.ad_type", "none"));

        playerApi.on("adblock", this.onAdBlock);
        playerApi.on("autoplayModeDetected", this.onAutoplayModeDetected);
        playerApi.on("cursorChanged", this.onCursorChanged);
        playerApi.on("fatalError", this.onFatalError);
        playerApi.on("playerError", this.onPlayerError);
        playerApi.on("playerStateChanged", this.onStateChange);
        playerApi.on("setupError", this.onSetupError);
        playerApi.setup(setup);
    },

    /**
     * Set the new mediaID as new source for the existing player to play.
     *
     * @private
     */
    changeMediaSource: function() {
        var $i18nService    = this.app.$.i18nService,
            $privacyService = this.app.$.privacyService,
            state           = this.state || {},
            media           = get(state, "media", {}),
            asset           = get(state, "asset", {}),
            session         = get(state, "watchingSession", {}),
            params          = get(state, "params", {}),
            mediaId         = get(state, "media.id", null),
            adType          = get(state, "session.ad_type", "none"),
            playerApi,
            mediaSource,
            autoplay,
            contentMetadata,
            offsetStartPosition,
            adSetup;

        // Already playing correct media
        if (mediaId === this.mediaId
            && adType === this.adType
            && this.isReady === 1) {
            this.get("autoplay") && this.tryAutoPlay();

            return;
        }

        // Determine autoplay
        autoplay = this.get("autoplay") || this.isPlaying;

        // Let's do this
        this.resetPlayerState();
        this.set("isReady", 0);

        // Find Player API
        playerApi = this.getPlayerApi();

        if (!playerApi) {
            this.controller.triggerHandler("onMediaPlayerError", {
                type:    "player_error",
                message: "Missing RMP IframePlayer API Instance"
            }, this);
            this.set("isReady", -1);

            return;
        }

        // Configuration
        mediaSource         = getSrcSetup(session, media);
        contentMetadata     = getContentMetadataSetup(media, {
            artwork:          true,
            poster:           true,
            thumbnail:        true,
            endOfVideoPoster: true,
            artworkRatio:     "square",
            posterRatio:      "square",
            thumbnailRatio:   "square"
        }).contentMetadata;
        offsetStartPosition = getOffsetStartPositionSetup(
            session,
            media,
            asset,
            params
        ).offsetStartPosition;
        adSetup             = getAdSetup(
            session,
            media,
            asset,
            $i18nService,
            $privacyService
        );

        // Set new media
        this.set("mediaId", mediaId);
        this.set("adType", adType);

        playerApi.changeMedia(
            mediaSource.src,
            contentMetadata,
            autoplay,
            offsetStartPosition,
            mediaSource.shakaDrm || {},
            adSetup
        );
    },

    /**
     * Verify that this player can be (re)used to play the supplied media.
     *
     * @param    {?Object}  mediaData    The media data
     * @param    {?Object}  sessionData  The watching session
     * @returns  {boolean}  Whether this tech can play the supplied media
     * @public
     */
    isReusableFor: function(mediaData, sessionData) {
        var mediaType = get(mediaData, "type", null),
            adType    = get(sessionData, "ad_type", "none");

        return this.isReusable
            && ["album_track", "audio", "podcast_episode"].includes(mediaType)
            && adType === this.adType;
    },

    /**
     * Get an instance of PlayerApi, to talk with the player in the iframe.
     *
     * @return {Object}  The PlayerApi instance
     */
    getPlayerApi: function() {
        return this.playerApi;
    },

    // Radiant MediaPlayer Event Handlers
    // --------------------------------

    /**
     * The player failed to start correctly.
     *
     * @param  {Object}  event  The error event
     * @private
     */
    onSetupError: function(event) {
        this.resetPlayerState();
        this.set("isReusable", false);
        this.controller.triggerHandler("onMediaPlayerError", {
            type:    "player_setup_error",
            message: event.message
        });
    },

    /**
     * Autoplay capability has become available.
     *
     * @private
     */
    onAutoplayModeDetected: function() {
        this.getPlayerApi()
            .getAutoplayMode()
            .then(this.bindSafely(function(autoplayMode) {
                this.set("autoplayMode", autoplayMode);
                this.tryAutoPlay();
            }))
            .catch(this.bindSafely(function(error) {
                var errorMsg = "Could not determine autoplay mode.";

                logger.error(errorMsg);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }
            }));
    },

    /**
     * PlayerApi broadcasted a new player state.
     *
     * @param  {Object}  event  The event
     * @private
     */
    onStateChange: function(event) {
        var state = event.state || {},
            mediaId;

        if (state.isReady !== this.get("isReady")) {
            this.set("isReady", state.isReady);

            // Player just entered ready state
            if (state.isReady === 1) {
                // Initialization just finished
                if (this.hasInitialized === 0) {
                    this.set("hasInitialized", 1);

                    // A switch of media source is already requested
                    if (this.changeMediaSourceOnInitialize) {
                        mediaId = get(this.state, "watchingSession.source", null);

                        // Only switch if the video is actually different
                        if (mediaId !== this.mediaId) {
                            this.stop();
                            this.changeMediaSource();
                        }

                        this.changeMediaSourceOnInitialize = false;
                    }

                    // No media source change scheduled. Try autoplaying the
                    // current video.
                    else {
                        this.tryAutoPlay();
                    }
                }

                // Player was already initialized. Try autoplaying current video.
                else {
                    this.tryAutoPlay();
                }
            }
        }

        if (state.hasStarted !== this.get("hasStarted")) {
            this.set("hasStarted", state.hasStarted);
        }

        if (state.isPlayingAds !== this.get("isPlayingAds")) {
            this.set("isPlayingAds", state.isPlayingAds);
            state.isPlayingAds && this.set("hasPlayedAds", true);
        }

        if (state.isPlaying !== this.get("isPlaying")) {
            this.set("isPlaying", state.isPlaying);
            state.isPlaying && this.app.$.autoplayTimerService.startTimer();
        }

        if (state.isSeekable !== this.get("isSeekable")) {
            this.set("isSeekable", state.isSeekable);
        }

        if (state.playerState !== this.get("playerState")) {
            this.set("playerState", state.playerState);
        }

        if (state.playerState === "ended") {
            this.onPlayerEnded();
        }
    },

    /**
     * PlayerApi broadcasted that the video has ended. If we're on an
     * ad-supported tier we want to immediately move on to the next video.
     *
     * @param  {Number}  event  The event
     * @private
     */
    onPlayerEnded: function() {
        var state     = this.state || {},
            nextMedia = get(state, "nextMedia", null);

        if (nextMedia && get(config, "autoplay.enabled", false)) {
            this.app.$.mediaPlayerManager.autoNavToMediaItem(nextMedia);
        }
        else {
            this.app.$.autoplayTimerService.stopTimer();
        }
    },

    /**
     * PlayerApi broadcasted a new cursor position.
     *
     * @param  {Number}  event  The event
     * @private
     */
    onCursorChanged: function(event) {
        this.set("cursor", event.cursor || null, true);
    },

    /**
     * The player broadcasted an error.
     *
     * @param  {Object}  event  The error event
     * @private
     */
    onPlayerError: function(event) {
        logger.error(event.error);

        // Can't send event to GA. Not worth showing an error screen.
        if (get(event, "playerError.code", null) === 13000) {
            return;
        }

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error("Radiant Media Player - Player Error"), {
                extra: {
                    event: circularSafeJSONStringify(event)
                }
            });
        }

        this.controller.triggerHandler("onMediaPlayerError", {
            type:    "player_error",
            message: event.message
        }, this);
    },

    /**
     * Ad-block event received.
     *
     * @private
     */
    onAdBlock: function() {
        this.controller.triggerHandler("onMediaPlayerError", {
            type: "ads_required_error"
        }, this);
    },

    /**
     * The player broadcasted a fatal error. Reuse is guaranteed to no longer be
     * possible.
     *
     * @param  {Object}  event  The error event
     * @private
     */
    onFatalError: function(event) {
        logger.error(event.error);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error("Radiant Media Player - Fatal Error"), {
                extra: {
                    event: circularSafeJSONStringify(event)
                }
            });
        }

        this.set("isReusable", false);
        this.controller.triggerHandler("onMediaPlayerError", {
            type:    "fatal_player_error",
            message: event.message
        }, this);
    },

    /**
     * Tell the autoplayTimerService there was a user interaction whenever the
     * player seeks or gets paused or unpaused.
     *
     * @param  {string}  oldState  The old state
     * @param  {string}  newState  The new state
     * @private
     */
    playerStateChanged: function(oldState, newState) {
        if (oldState !== newState && (oldState === "paused" || newState === "paused")) {
            this.app.$.autoplayTimerService.resetTimer();
        }
    },

    /**
     * Try to autoplay if the player is ready and autoplayMode should enable us
     * to play with audio.
     *
     * @private
     */
    tryAutoPlay: function() {
        if (!this.get("autoplay")) {
            return;
        }

        if (this.get("hasStarted")) {
            return;
        }

        if (this.get("isReady") === 1 && this.get("autoplayMode") === "autoplay-with-sound") {
            this.play();
        }
    },

    // AudioPlayer API
    // --------------------------------

    /**
     * Start playing
     *
     * @public
     */
    play: function() {
        var api;

        if (this.get("isReady") === 1) {
            api = this.getPlayerApi();
            api && api.play();
        }
    },

    /**
     * Move to a certain cursor position (in seconds)
     *
     * @param  {number}  cursor  The cursor position to seek to
     *
     * @public
     */
    seek: function(cursor) {
        var api;

        if (this.get("isReady") === 1 && this.get("isSeekable")) {
            api = this.getPlayerApi();
            api && api.seek(cursor);
        }
    },

    /**
     * Pause the player
     *
     * @public
     */
    pause: function() {
        var api;

        if (this.get("isReady") === 1) {
            api = this.getPlayerApi();
            api && api.pause();
        }
    },

    /**
     * Stop the player
     *
     * @public
     */
    stop: function() {
        var api;

        if (this.get("isReady") === 1) {
            // TODO: Do we need to call stopAds() if ads are playing?
            api = this.getPlayerApi();
            api && api.stop();
        }
    },

    /**
     * Returns the current point on the player's timeline in seconds.
     *
     * @return  {?number}  The cursor position
     *
     * @public
     */
    getCursor: function() {
        return new Promise(function(resolve) {
            if (this.get("isReady") === 1) {
                resolve(this.cursor >= 0 ? this.cursor : null);
            }
            else {
                resolve(null);
            }
        });
    },

    /**
     * Returns the duration of the audio in seconds.
     *
     * @return  {?number}  The duration of the current asset
     *
     * @public
     */
    getLength: function() {
        return new Promise(function(resolve) {
            if (this.get("isReady") === 1) {
                resolve(this.duration >= 0 ? this.duration : null);
            }
            else {
                resolve(null);
            }
        });
    },

    // Destruction
    // --------------------------------

    /**
     * Iframes cannot be moved around the DOM without reloading. As a result,
     * unmounting it makes it no longer reusable.
     *
     * @private`
     */
    beforeUnmount: function() {
        this.set("isReusable", false);
    },

    /**
     * Destroys the player instance.
     *
     * @private
     */
    destroyPlayer: function() {
        var playerApi = this.getPlayerApi(),
            iframe    = this.get("iframe");

        this.stop();

        if (playerApi) {

            // Remove event listeners
            playerApi.off("adblock", this.onAdBlock);
            playerApi.off("autoplayModeDetected", this.onAutoplayModeDetected);
            playerApi.off("cursorChanged", this.onCursorChanged);
            playerApi.off("fatalError", this.onFatalError);
            playerApi.off("playerError", this.onPlayerError);
            playerApi.off("playerStateChanged", this.onStateChange);
            playerApi.off("setupError", this.onSetupError);

            // Destroy API
            playerApi.destroy();
        }
        this.set("playerApi", null);

        // Teardown iframe
        if (iframe && iframe.parentNode) {
            iframe.remove();
        }
        this.set("iframe", null);

        // Reset internal state
        this.resetPlayerState();
    },

    /**
     * Destroy the Radiant MediaPlayer when this instance gets destroyed.
     *
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            this.destroyPlayer();

            // Ensures we trigger a request to hide the loading graphic.
            this.set("isLoadingPlayerApi", false);
            sup.apply(this, arguments);
        };
    })

});
